import { useEffect } from 'react';
import { recoverCart } from '../api/cartRecovery';
import { readPendingLine, type PendingLine } from './pendingLine';

/** A checkout token as the API writes it: long and hexadecimal, so anything else in the address is ignored. */
const TOKEN = /^[a-f0-9]{16,128}$/i;

const GONE = 'This cart is no longer available. Its items may have sold out, or the order was already placed.';

// Takes the recovery token out of the address, so a reload or a shared link does not refill the cart again.
function takeToken(): string | null {
  const url = new URL(window.location.href);
  const token = url.searchParams.get('recover');
  if (token === null) return null;
  url.searchParams.delete('recover');
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  return TOKEN.test(token) ? token : null;
}

// Refills the cart from an abandoned checkout's recovery link (?recover=<token>) once, then opens it; a link that
// no longer works opens the cart saying so.
export function useRecoveredCart(setLines: (lines: PendingLine[]) => void, fail: (message: string) => void, open: () => void): void {
  useEffect(() => {
    const token = takeToken();
    if (!token) return;
    recoverCart(token)
      .then(({ lines }) => {
        const valid = lines.map((line) => readPendingLine(JSON.stringify(line))).filter((line): line is PendingLine => line !== null);
        if (valid.length) setLines(valid); else fail(GONE);
      })
      .catch(() => fail(GONE))
      .finally(open);
    // Runs once per page load: the token is gone from the address after the first read, and the callbacks the
    // cart hands in change on every render while the token does not.
  }, []);
}
